import { useCallback, useState } from 'react';

export interface UseCursorPagerOptions {
  /** Cursor currently in the URL. */
  cursor: string | undefined;
  /** Navigate to a cursor (undefined = first page). Usually `navigate({ search: prev => ({...prev, cursor}) })`. */
  onNavigate: (cursor: string | undefined) => void;
  /** Anything that resets pagination when it changes: filters, sort, query, limit. */
  resetKey: string;
}

export interface CursorPager {
  goNext: (nextCursor: string) => void;
  goPrev: () => void;
  goFirst: () => void;
  canPrev: boolean;
}

/**
 * Cursor APIs only hand out `nextCursor`, so "Previous" needs a client-side stack of the
 * cursors already visited. The stack lives in memory; a refresh keeps the current page and
 * offers "First page" instead of "Previous".
 */
export function useCursorPager({
  cursor,
  onNavigate,
  resetKey,
}: UseCursorPagerOptions): CursorPager {
  const [stack, setStack] = useState<Array<string | undefined>>([]);
  // Reset the stack when filters change (derived-state pattern, no effect needed).
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setStack([]);
  }

  const goNext = useCallback(
    (nextCursor: string) => {
      setStack((s) => [...s, cursor]);
      onNavigate(nextCursor);
    },
    [cursor, onNavigate],
  );

  const goPrev = useCallback(() => {
    const prev = stack.at(-1);
    setStack((s) => s.slice(0, -1));
    onNavigate(prev);
  }, [onNavigate, stack]);

  const goFirst = useCallback(() => {
    setStack([]);
    onNavigate(undefined);
  }, [onNavigate]);

  return { goNext, goPrev, goFirst, canPrev: stack.length > 0 };
}
